/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import { theme } from "styles/theme";
import {
  Clipboard,
  Container,
  Heading,
  LinkContainer,
  LinkLabel,
  Name,
  ProfileContainer,
  SectionGroup,
  SectionTitle,
  VisitInfoPreview,
} from "./index.styles";
import { copyToClipboard } from "utils/copyToClipboard";
import Avatar from "uis/avatar";
import Icon from "uis/Icon";
import { member, userInfo, workspace } from "recoil/recoilState";
import { useRecoilValue } from "recoil";
import VisitList from "./visit-list";
import { TVisit } from "apis/visitors/type";
import VisitPending from "./visit-pending";
import OnboardingModal from "../onboarding-modal/onboarding-modal";
import { encrypt } from "utils/crypto";
import { gaEvent } from "utils/ga-utils";
import UI from "@mile-meeting/mile-ui";
import { useToast } from "hook/useToast";
import { getPendingRecordList } from "apis/visitors/record";
import { getVisitorsModalMetaData } from "apis/visitors/modal";

export default function MyVisitManage() {
  const [section, setSection] = useState<string>("오늘 일정");
  const { user } = useRecoilValue(userInfo);
  const [pending, setPending] = useState<TVisit[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totals, setTotals] = useState<number>();
  const [showOnboardingModal, setShowOnboardingModal] = useState<boolean>(false);
  const { showToast } = useToast();
  const workspaceInfo = useRecoilValue(workspace);
  const memberInfo = useRecoilValue(member);
  const [isFirstOnboard, setIsFirstOnboard] = useState<boolean>(true);

  //* visitors 온보딩 모달이 뜨지 않았을 때만 뜨도록
  const openOnboardingModal = async () => {
    const res = await getVisitorsModalMetaData();
    if (res.data) {
      if (!res.data.onboardingModalDismissed) {
        setShowOnboardingModal(true);
        setIsFirstOnboard(true);
      }
    }
  };

  useEffect(() => {
    openOnboardingModal();
  }, []);

  useEffect(() => {
    getPendingRecordList(1).then((res) => {
      if (res.data) {
        setPending(res.data.visitRecords);
        setPage(1);
        setTotals(res.data.totalPageNumber);
      }
    });
  }, [section]);

  const updatePendingRecord = (page: number) => {
    getPendingRecordList(page).then((res) => {
      if (res.data) {
        setPending(res.data.visitRecords);
        setPage(page);
        setTotals(res.data.totalPageNumber);
      }
    });
  };

  return (
    <Container>
      <Heading>
        <UI.Typography
          type="title03"
          fontStyle="bold"
          color="var(--alias-color-label-neutral)"
          style={{
            padding: "24px 0 24px 20px",
          }}
        >
          나의 방문 관리
        </UI.Typography>
        <UI.IconButton.Normal
          icon="info"
          size={24}
          color="var(--alias-color-label-assistive)"
          onClick={() => {
            setShowOnboardingModal(true);
            gaEvent("나의 방문 관리 - 도움말 선택");
          }}
        />
      </Heading>

      <OnboardingModal
        isFirst={isFirstOnboard}
        modalIsOpen={showOnboardingModal}
        closeModal={() => {
          setShowOnboardingModal(false);
        }}
      ></OnboardingModal>

      <ProfileContainer>
        <Avatar
          image={memberInfo?.image}
          styles={css`
            width: 100px;
            height: 100px;
          `}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 12px;
          `}
        >
          <div
            css={css`
              padding: 6px 0;
              gap: 8px;
              display: flex;
              flex-direction: column;
            `}
          >
            <Name>{user?.name}</Name>
            <div
              css={css`
                display: flex;
                gap: 12px;
              `}
            >
              <LinkContainer
                onClick={() => {
                  window.open(
                    `${process.env.REACT_APP_VISITORS_WEB_URL}/${memberInfo?.publicId && memberInfo.publicId.replace("@", "/")}`
                  );
                }}
              >
                <LinkLabel>
                  {process.env.REACT_APP_VISITORS_WEB_URL}/{memberInfo?.publicId}
                </LinkLabel>
              </LinkContainer>
              <Clipboard
                onClick={() => {
                  copyToClipboard(
                    `${process.env.REACT_APP_VISITORS_WEB_URL}/${memberInfo?.publicId && memberInfo.publicId.replace("@", "/")}`
                  );
                  showToast("링크가 복사되었습니다.", "success");
                }}
              >
                <Icon name={"link"} width={20} color={theme.primary500} />
                링크복사
              </Clipboard>
            </div>
            <VisitInfoPreview
              onClick={() => {
                if (workspaceInfo?.id) {
                  window.open(
                    `${process.env.REACT_APP_VISITORS_WEB_URL}/default/${encodeURIComponent(encrypt(workspaceInfo.id))}`
                  );
                }
                gaEvent("나의 방문 관리 - 기업 방문 메세지 프리뷰 선택");
              }}
            >
              💬 {workspaceInfo?.name}의 방문 안내 메세지 확인하기
            </VisitInfoPreview>
          </div>
        </div>
      </ProfileContainer>
      <SectionGroup>
        {["예정된 일정", "오늘 일정", "지난 일정", `방문 요청 내역 (${pending.length})`].map((item, index) => {
          return (
            <SectionTitle
              key={index}
              isPressed={item.includes(section)}
              onClick={() => {
                if (item.includes("방문 요청 내역")) {
                  setSection("방문 요청 내역");
                } else setSection(item);
              }}
            >
              {item}
            </SectionTitle>
          );
        })}
        <UI.Button
          title="일정 예약하기"
          onClick={() => (window.location.href = "/visitors/register")}
          style={{
            marginLeft: "auto",
            position: "absolute",
            right: 0,
            bottom: 12,
          }}
        />
      </SectionGroup>

      {!(section === "방문 요청 내역") && <VisitList type={section} />}
      {section === "방문 요청 내역" && (
        <VisitPending records={pending} onChange={updatePendingRecord} page={page} totalPage={totals} />
      )}
    </Container>
  );
}
